<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue';
import { useTrip } from '@/stores/trip';
import { useMap } from '@/composables/useMap';
import { useToast } from '@/composables/useToast';
import { STATIONS, STATION_INDEX, GRAPH } from '@/data/graph';
import { dijkstra } from '@/core/pathfinding';
import { getCost, getUsedMode } from '@/core/edges';
import { bandFor, fmtTime } from '@/core/format';
import { haversine } from '@/core/geo';
import { BANDS } from '@/core/constants';

/**
 * Carte interactive Leaflet.
 *
 * Responsabilités :
 *   - Initialiser la carte (via useMap)
 *   - Re-rendre marqueurs et tracés à chaque mutation du store
 *   - Gérer le clic sur les marqueurs (popup avec actions "Ajouter à l'itinéraire")
 *
 * Sur MOBILE, la carte démarre en mode "désactivé" (drag off) pour ne pas
 * capturer le scroll de la page. Un overlay invite à toucher pour explorer.
 *
 * IMPORTANT : on ne fait PAS de v-for sur les marqueurs (Leaflet n'est pas réactif).
 * On a une fonction `redraw()` qui clear + redessine. Coût : ~200 marqueurs à chaque
 * mutation. C'est rapide (~10ms) et largement assez performant pour cette UI.
 */
const emit = defineEmits<{
  /** Émis quand un marqueur de carte est ouvert (utilisé par le drawer pour se replier) */
  popupOpen: [];
  /** Émis quand on clique en dehors d'un marqueur (utilisé pour replier le drawer) */
  mapClick: [];
}>();

defineExpose({
  invalidateSize: () => map.invalidateSize(),
});

const trip = useTrip();
const { show: showToast } = useToast();
const mapEl = ref<HTMLElement | null>(null);
const hintHidden = ref(false);
/** Zoom courant — utilisé pour adapter la taille/visibilité des marqueurs */
const currentZoom = ref(4);
/** Vrai sur mobile uniquement, tant que l'utilisateur n'a pas explicitement activé la carte */
const mobileLocked = ref(false);

const map = useMap({ elRef: mapEl });

function isMobileNow() {
  return window.matchMedia('(max-width: 699px)').matches;
}

// ---------- Activation mobile (tap-to-explore) ----------

function activateMobileMap() {
  if (!isMobileNow()) return;
  mobileLocked.value = false;
  map.activate();
}

function deactivateMobileMap() {
  if (!isMobileNow()) return;
  mobileLocked.value = true;
  map.deactivate();
}

// Si la fenêtre redimensionne (rotation, etc.), on recalcule le mode
function handleResize() {
  if (isMobileNow()) {
    if (!map.isActive()) mobileLocked.value = true;
  } else {
    mobileLocked.value = false;
    map.activate();
  }
}

// ---------- Cycle de vie ----------

onMounted(() => {
  // Auto-hide du hint après 10s
  setTimeout(() => (hintHidden.value = true), 10_000);

  // Sur mobile, la carte démarre verrouillée pour permettre le scroll de la page
  if (isMobileNow()) {
    mobileLocked.value = true;
    // useMap a déjà initialisé avec dragging: false sur mobile
  }

  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleResize);

  // Écouteurs Leaflet
  map.on('popupopen', (e) => {
    const popup = (e as L.PopupEvent).popup;
    const node = (popup as L.Popup & { _contentNode?: HTMLElement })._contentNode;
    if (node) attachPopupHandlers(node);
    emit('popupOpen');
  });
  map.on('click', () => emit('mapClick'));

  // Redraw quand le zoom change — pour adapter la taille/visibilité des marqueurs,
  // SANS recadrer (sinon le zoom de l'utilisateur serait écrasé).
  map.on('zoomend', () => {
    const z = map.map.value?.getZoom();
    if (z !== undefined && z !== currentZoom.value) {
      currentZoom.value = z;
      redraw({ fit: false });
    }
  });

  // Premier redraw — `useMap` initialise la carte dans son propre onMounted, dont
  // l'exécution n'est pas garantie d'être terminée ici. Le setTimeout 0 attend
  // la prochaine micro-tâche, ce qui suffit en pratique (Vue exécute tous les
  // onMounted dans le même tick avant de rendre la main).
  setTimeout(() => redraw({ fit: true }), 0);
});

/**
 * À partir du zoom courant, renvoie un facteur d'échelle pour les marqueurs
 * et un seuil pour décider si on affiche les marqueurs "faint" (hors limite).
 *
 * Échelle :
 *   - zoom ≤ 3 (vue continentale) : marqueurs réduits, "faint" cachés
 *   - zoom 4-5 (vue régionale)    : taille normale, "faint" cachés
 *   - zoom 6-8 (vue pays/région)  : taille normale, "faint" visibles
 *   - zoom ≥ 9 (vue locale)       : taille normale
 */
function zoomAdaptation() {
  const z = currentZoom.value;
  return {
    /** Multiplicateur de taille des marqueurs city (0.7 à très bas zoom, 1 sinon) */
    citySizeFactor: z <= 3 ? 0.7 : 1,
    /** Si false, on ne dessine pas les marqueurs "faint" (au-delà de maxHours) */
    showFaint: z >= 6,
  };
}

// ---------- Redraw réactif ----------
//
// Deux watchers séparés pour distinguer :
//
//   1) Composition de l'itinéraire (ajout/retrait/ré-ordre d'étape) → redraw + recadrage.
//      C'est le seul cas où l'utilisateur s'attend à voir la carte se recentrer
//      automatiquement pour englober tous ses points.
//
//   2) Changement de filtre (mode, maxHours, directOnly, origin) → redraw sans recadrage.
//      L'utilisateur est probablement en train d'explorer la carte à un certain niveau
//      de zoom ; on ne lui vole pas sa position visuelle.

// Watcher 1 : composition de l'itinéraire — redessine ET recadre
watch(
  () => trip.state.routeStops,
  () => redraw({ fit: true }),
  { deep: true, immediate: false },
);

// Watcher 2 : filtres et mode — redessine SANS recadrer
watch(
  () => [
    trip.state.routeMode,
    trip.state.routeDirectOnly,
    trip.state.maxHours,
    trip.state.origin,
  ],
  () => redraw({ fit: false }),
  { immediate: false },
);

function redraw(opts: { fit?: boolean } = {}) {
  if (!map.map.value) return;
  const shouldFit = opts.fit !== false;
  map.clearLayers();

  const stops = trip.state.routeStops;
  const hasStops = stops.length > 0;
  let refStopId = hasStops ? stops[stops.length - 1].id : trip.state.origin;

  if (refStopId && STATION_INDEX[refStopId] === undefined) {
    refStopId = STATION_INDEX['lyon'] !== undefined ? 'lyon' : '';
    if (!hasStops) trip.setOrigin(refStopId);
  }

  // Pré-calculs depuis le refStop
  const reachInfo = refStopId ? dijkstra(refStopId, trip.state.routeMode) : null;

  // Si filtre "direct", on prépare un index des voisins directs uniquement.
  // En mode "Nuit" strict, on filtre en plus pour ne garder que les voisins
  // joignables par un train de nuit qualifiant (≥ 6h sur l'arête).
  let directNeighbors: Record<string, number> | null = null;
  if (refStopId && trip.state.routeDirectOnly) {
    directNeighbors = {};
    const isNightMode = trip.state.routeMode === 'night';
    GRAPH[refStopId].forEach((nb) => {
      const cost = getCost(nb, trip.state.routeMode);
      if (cost == null) return;
      const used = getUsedMode(nb, trip.state.routeMode);
      if (!used) return;

      // Filtre strict mode nuit : on ne garde que les arêtes train de nuit ≥ 6h
      if (isNightMode) {
        const qualifies =
          used === 'train' &&
          nb.night === true &&
          nb.train != null &&
          nb.train >= 360;
        if (!qualifies) return;
      }

      if (directNeighbors![nb.to] === undefined || cost < directNeighbors![nb.to]) {
        directNeighbors![nb.to] = cost;
      }
    });
  }

  const stopIds = stops.map((s) => s.id);
  const bounds: [number, number][] = [];
  const { citySizeFactor, showFaint } = zoomAdaptation();
  const cityIconSize = Math.round(14 * citySizeFactor);

  // ----- Marqueurs ville par ville -----
  STATIONS.forEach((s) => {
    const stopIdx = stopIds.indexOf(s.id);
    const isStop = stopIdx !== -1;
    const isRefOrigin = !hasStops && s.id === refStopId;

    let iconHtml = '';
    let iconSize = cityIconSize;
    let tooltip = '';
    let zIndexOffset = 100;
    let skipMarker = false;
    let popupHtml: string | undefined;

    if (isStop) {
      // Affiche tous les numéros si la même ville est plusieurs fois (boucle)
      const allIdx = stopIds
        .map((id, i) => (id === s.id ? i + 1 : -1))
        .filter((x) => x !== -1);
      const numLabel = allIdx.length > 1 ? allIdx.join('·') : String(allIdx[0]);
      iconHtml = `<div class="station-marker-route">${numLabel}</div>`;
      iconSize = 20;
      tooltip = allIdx.map((n) => `${n}. ${s.name}`).join(' / ');
      zIndexOffset = 500;
      bounds.push(s.coords);
    } else if (isRefOrigin) {
      iconHtml = '<div class="station-marker-origin"></div>';
      iconSize = 24;
      tooltip = `${s.name} · point de départ`;
      zIndexOffset = 400;
    } else if (directNeighbors) {
      const t = directNeighbors[s.id];
      if (t === undefined || t / 60 > trip.state.maxHours) {
        skipMarker = true;
      } else {
        const bIdx = bandFor(t);
        if (bIdx >= 0) {
          iconHtml = `<div class="station-marker-city" style="background:${BANDS[bIdx].color}"></div>`;
        } else {
          iconHtml = '<div class="station-marker-reachable"></div>';
        }
        const refName = STATIONS[STATION_INDEX[refStopId]].name;
        tooltip = `${s.name} · ${fmtTime(t)} direct depuis ${refName}`;
      }
    } else if (reachInfo) {
      const t = reachInfo.dist[s.id];

      // Mode "Nuit" STRICT : une ville n'est considérée comme atteignable
      // que si son chemin optimal inclut au moins un segment train de nuit
      // qualifiant (>= 6h). Sinon, en mode Nuit l'algo afficherait toutes
      // les villes accessibles en train de jour, ce qui n'a aucun sens pour
      // un mode dont le but est de planifier en train de nuit.
      const isNightMode = trip.state.routeMode === 'night';
      const hasNightInPath = reachInfo.hasNightTrain[s.id] === true;
      if (isNightMode && !hasNightInPath) {
        skipMarker = true;
      } else if (t === Infinity || t === 0) {
        skipMarker = true;
      } else if (t / 60 > trip.state.maxHours) {
        // Hors limite : on cache complètement en bas zoom pour ne pas empiler les "faint"
        if (!showFaint) {
          skipMarker = true;
        } else {
          iconHtml = '<div class="station-marker-faint"></div>';
          iconSize = 8;
          tooltip = `${s.name} · ${fmtTime(t)} (au-delà de la limite)`;
        }
      } else {
        const bIdx = bandFor(t);
        if (bIdx >= 0) {
          iconHtml = `<div class="station-marker-city" style="background:${BANDS[bIdx].color}"></div>`;
        } else {
          iconHtml = '<div class="station-marker-reachable"></div>';
        }
        const refName = STATIONS[STATION_INDEX[refStopId]].name;
        tooltip = `${s.name} · ${fmtTime(t)} depuis ${refName}`;
      }
    } else {
      if (!showFaint) {
        skipMarker = true;
      } else {
        iconHtml = '<div class="station-marker-faint"></div>';
        iconSize = 8;
        tooltip = s.name;
      }
    }

    if (skipMarker) return;

    // Popup : actions disponibles selon le contexte. On passe `reachInfo` et
    // `refStopId` pour réutiliser le résultat Dijkstra déjà calculé en haut
    // de redraw — sinon on referait 200 dijkstras (1 par marqueur) à chaque
    // redessin, ce qui est très lourd sur mobile.
    popupHtml = buildPopupHtml(s.id, hasStops, refStopId, reachInfo);

    map.addMarker(s.coords, {
      iconHtml,
      iconSize,
      tooltip,
      zIndexOffset,
      popupHtml,
    });
  });

  // ----- Tracés des segments d'itinéraire -----
  for (let i = 0; i < stops.length - 1; i++) {
    const path = trip.calendar.value.cal[i]?.segmentPath;
    if (!path) continue;
    path.segments.forEach((seg) => {
      const fromS = STATIONS[STATION_INDEX[seg.from]];
      const toS = STATIONS[STATION_INDEX[seg.to]];
      map.addSegmentLine(fromS.coords, toS.coords, seg);
    });
  }

  // ----- Cadrage -----
  // Uniquement quand la composition de l'itinéraire change (pas sur un simple zoom)
  if (shouldFit) {
    if (bounds.length >= 2) {
      map.fitBounds(bounds);
    } else if (bounds.length === 1) {
      map.setView(bounds[0]);
    } else if (refStopId && STATION_INDEX[refStopId] !== undefined) {
      map.setView(STATIONS[STATION_INDEX[refStopId]].coords);
    }
  }
}

// ---------- Popups ----------

function buildPopupHtml(
  stationId: string,
  hasStops: boolean,
  refStopId: string,
  reachInfo: ReturnType<typeof dijkstra> | null,
): string {
  const s = STATIONS[STATION_INDEX[stationId]];

  // Infos de trajet depuis refStop si ce n'est pas une étape et pas la ref.
  // Réutilise `reachInfo` (déjà calculé par redraw) pour éviter un appel
  // Dijkstra supplémentaire à chaque popup.
  const stops = trip.state.routeStops;
  const isStop = stops.some((st) => st.id === stationId);
  const isRef = stationId === refStopId;

  let info = '';
  if (refStopId && !isStop && !isRef && reachInfo) {
    const t = reachInfo.dist[stationId];
    if (t !== Infinity && t > 0) {
      const refS = STATIONS[STATION_INDEX[refStopId]];
      const km = haversine(refS.coords, s.coords);
      const isDirect = reachInfo.direct[stationId];
      // `night` ici = toute la chaîne est composée de trains de nuit ET le
      // segment global est suffisamment long pour qu'on dorme vraiment.
      // Seuil 6h aligné avec NIGHT_SEGMENT_MIN_DURATION dans constants.ts.
      const isNightTrip = reachInfo.night[stationId] && t >= 360;
      info = `
        <div class="flex items-center gap-2 mt-2 text-sm">
          <span class="font-mono font-medium text-accent">${fmtTime(t)}</span>
          <span class="text-text-muted">${isDirect ? 'liaison directe' : 'avec correspondance'}</span>
        </div>
        <div class="text-xs text-text-muted mt-1">${km.toLocaleString('fr-FR')} km</div>
        ${
          isNightTrip
            ? '<div class="text-xs text-mode-night mt-1"><i class="ti ti-moon"></i> Train de nuit</div>'
            : ''
        }
      `;
    } else if (t === Infinity) {
      info = '<div class="text-xs text-text-muted mt-2">Non desservi depuis le point actuel</div>';
    }
  }

  const actions = `
    <div class="flex flex-wrap gap-2 mt-3">
      <button
        type="button"
        data-action="add-route"
        data-id="${stationId}"
        class="inline-flex items-center gap-1 px-3 py-1.5 bg-accent text-white text-xs font-medium rounded-md hover:bg-accent-hover transition-colors"
      >
        <i class="ti ti-plus"></i> Ajouter à mon itinéraire
      </button>
      ${
        !hasStops
          ? `<button
              type="button"
              data-action="set-origin"
              data-id="${stationId}"
              class="inline-flex items-center gap-1 px-3 py-1.5 bg-surface border border-DEFAULT text-xs font-medium rounded-md hover:bg-surface-muted transition-colors"
            >
              <i class="ti ti-map-pin"></i> Voir les trajets depuis ici
            </button>`
          : ''
      }
    </div>
  `;

  return `
    <div>
      <div class="font-medium">${s.name}</div>
      ${info}
      ${actions}
    </div>
  `;
}

function attachPopupHandlers(node: HTMLElement) {
  node.querySelectorAll<HTMLButtonElement>('[data-action]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const action = btn.dataset.action;
      const id = btn.dataset.id;
      if (!id) return;
      if (action === 'set-origin') {
        trip.setOrigin(id);
        map.closePopup();
      } else if (action === 'add-route') {
        const ok = trip.addStop(id);
        if (!ok) {
          showToast('Cette ville est déjà la dernière étape', 'error');
        }
        map.closePopup();
      }
    });
  });
}
</script>

<template>
  <section class="relative h-full">
    <div ref="mapEl" class="absolute inset-0" role="application" aria-label="Carte interactive de l'Europe"></div>

    <!-- Overlay tap-to-explore (mobile uniquement, tant que la carte est verrouillée) -->
    <button
      v-if="mobileLocked"
      type="button"
      class="md:hidden absolute inset-0 z-[850] flex flex-col items-center justify-center gap-3 bg-black/20 backdrop-blur-[1px] active:bg-black/30 transition-colors"
      aria-label="Activer la carte"
      @click="activateMobileMap"
    >
      <span class="px-4 py-2.5 bg-surface text-text font-medium rounded-full shadow-lg inline-flex items-center gap-2 text-sm">
        <i class="ti ti-hand-finger" aria-hidden="true"></i>
        Toucher pour explorer la carte
      </span>
      <span class="text-xs text-white/90 bg-black/40 px-2.5 py-1 rounded-md backdrop-blur">
        Faites défiler la page librement
      </span>
    </button>

    <!-- Bouton "verrouiller" la carte (visible quand activée sur mobile) -->
    <button
      v-if="!mobileLocked"
      type="button"
      class="md:hidden absolute top-4 right-4 z-[1000] w-9 h-9 inline-flex items-center justify-center bg-surface/95 backdrop-blur rounded-full shadow-md text-text-muted hover:text-text"
      aria-label="Verrouiller la carte"
      title="Verrouiller la carte (pour scroller la page)"
      @click="deactivateMobileMap"
    >
      <i class="ti ti-lock" aria-hidden="true"></i>
    </button>

    <Transition
      enter-active-class="transition duration-300"
      leave-active-class="transition duration-300"
      enter-from-class="opacity-0 -translate-y-2"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div
        v-if="!hintHidden"
        class="absolute top-4 left-4 right-16 md:right-4 md:left-1/2 md:-translate-x-1/2 md:max-w-md z-[800] flex items-start gap-2 px-3 py-2.5 bg-surface/95 backdrop-blur border border-DEFAULT rounded-md shadow-md text-sm"
      >
        <i class="ti ti-info-circle text-accent mt-0.5" aria-hidden="true"></i>
        <span class="flex-1">
          Cliquez sur une ville pour voir les détails et l'ajouter à votre itinéraire.
        </span>
        <button
          type="button"
          class="text-text-faint hover:text-text"
          aria-label="Fermer"
          @click="hintHidden = true"
        >
          <i class="ti ti-x" aria-hidden="true"></i>
        </button>
      </div>
    </Transition>
  </section>
</template>
